import {
    Box,
    Input,
    Radio,
    RadioGroup,
    Text,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import PartBayButton from '../PartBayButton';
import {
    BuyPriceMethod,
    LocationEntry,
    OrgInventorySettings,
    PartFulfillmentContact,
} from '../../hooks/useGetSettings';
import {
    useSettingsDraft,
    useSettingsDraftValidator,
} from '../../pages/settings/SettingsDraftContext';
import PartFulfillmentContactsField from './PartFulfillmentContactsField';
import {
    SETTINGS_SUBTABLE_PAGE_SIZE,
    SettingsCard,
    SettingsInlineTextEdit,
    SettingsRow,
    SettingsRowActions,
    SettingsSegmentedControl,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from './primitives';
import type { SettingsSegment } from './primitives';

interface InventorySettingsProps {
    inventory?: OrgInventorySettings | null;
    locations: Array<LocationEntry>;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BUY_PRICE_SEGMENTS: ReadonlyArray<SettingsSegment<BuyPriceMethod>> = [
    {
        value: 'lifo',
        label: 'LIFO',
        tooltip: 'Last-in-first-out: the latest inventoried part cost is used when selling a part.',
    },
    {
        value: 'fifo',
        label: 'FIFO',
        tooltip: 'First-in-first-out: the cost of the part inventoried first is used.',
    },
];

const BUY_PRICE_DESCRIPTION =
    'LIFO is last-in-first-out, which means the latest inventoried part cost is used when ' +
    'selling a part. FIFO is first-in-first-out, which means the cost of the part inventoried ' +
    'first is used.';

const InventorySettings = ({ inventory, locations }: InventorySettingsProps) => {
    // The whole screen edits one `inventory` subdocument, so it is held on the
    // shared draft: the page's save bar writes all three cards at once, and
    // Discard snaps every one of them back to the server value.
    const { getDomain, setDomain } = useSettingsDraft();

    const serverValue = useMemo<OrgInventorySettings>(
        () => ({
            businessLines: inventory?.businessLines ?? [],
            defaultBusinessLine: inventory?.defaultBusinessLine ?? null,
            partFulfillmentContacts: inventory?.partFulfillmentContacts ?? [],
            buyPriceMethod: inventory?.buyPriceMethod ?? 'lifo',
        }),
        [inventory],
    );
    const draft = getDomain('inventory', serverValue) ?? serverValue;

    const lines = draft.businessLines;
    const defaultBusinessLine = draft.defaultBusinessLine ?? '';
    const buyPriceMethod: BuyPriceMethod = draft.buyPriceMethod ?? 'lifo';
    const contacts = draft.partFulfillmentContacts ?? [];

    // Adding a line takes one field, so Add appends an entry row to the table
    // itself. It is held here rather than on the draft, so an abandoned row
    // never counts as an unsaved change or blocks the save bar.
    const [draftName, setDraftName] = useState<string | null>(null);

    const update = (patch: Partial<OrgInventorySettings>) => {
        setDomain('inventory', { ...draft, ...patch });
    };

    const emailsFor = (locationId: string) =>
        contacts.filter((c) => c.locationId === locationId).map((c) => c.email);

    const sortedLocations = useMemo(
        () =>
            [...locations].sort((a, b) =>
                a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
            ),
        [locations],
    );

    const linePaging = useSubTablePagination(lines);
    const locationPaging = useSubTablePagination(sortedLocations);

    // Inline editors reject empty, duplicate, and malformed entries before they
    // land on the draft, so these checks are a backstop for the one thing the
    // cells cannot see: a list left without a default.
    useSettingsDraftValidator('inventory', () => {
        const names = lines.map((l) => l.name.trim());
        if (names.some((name) => !name)) return 'Business line names cannot be empty.';
        if (new Set(names).size !== names.length) return 'Business line names must be unique.';
        if (names.length > 0 && !names.includes(defaultBusinessLine.trim())) {
            return 'Select a default business line.';
        }
        if (contacts.some((c) => !EMAIL_PATTERN.test(c.email))) {
            return 'Enter a valid email for each part fulfillment contact.';
        }
        return null;
    });

    /** Empty or already taken, ignoring the row being renamed. */
    const businessLineNameError = (name: string, skipIndex?: number): string | null => {
        if (!name) return 'Enter a name.';
        const taken = lines.some(
            (line, i) => i !== skipIndex && line.name.trim().toLowerCase() === name.toLowerCase(),
        );
        return taken ? 'That name already exists.' : null;
    };

    const openAdd = () => {
        setDraftName('');
        // The entry row lands at the end of the list, so jump to the page it
        // will appear on.
        linePaging.setCurrentPage(linePaging.totalPages);
    };

    const handleAdd = () => {
        const name = (draftName ?? '').trim();
        if (businessLineNameError(name)) return;
        update({
            businessLines: [...lines, { name }],
            // First line added becomes the default, so the list is never
            // configured without one.
            defaultBusinessLine: lines.length === 0 ? name : draft.defaultBusinessLine,
        });
        setDraftName(null);
    };

    const handleRename = (index: number, name: string) => {
        const previousName = lines[index]?.name;
        update({
            businessLines: lines.map((line, i) => (i === index ? { name } : line)),
            defaultBusinessLine:
                previousName && defaultBusinessLine === previousName
                    ? name.trim()
                    : draft.defaultBusinessLine,
        });
    };

    const handleRemove = (index: number) => {
        const removed = lines[index]?.name;
        const next = lines.filter((_, i) => i !== index);
        update({
            businessLines: next,
            defaultBusinessLine:
                next.length === 0
                    ? null
                    : removed && defaultBusinessLine === removed
                        ? next[0].name.trim()
                        : draft.defaultBusinessLine,
        });
    };

    const handleEmailsChange = (locationId: string, emails: Array<string>) => {
        const others = contacts.filter((c) => c.locationId !== locationId);
        const next: Array<PartFulfillmentContact> = [
            ...others,
            ...emails
                .map((email) => email.trim())
                .filter(Boolean)
                .map((email) => ({ locationId, email })),
        ];
        update({
            partFulfillmentContacts: next.sort((a, b) => {
                const loc = a.locationId.localeCompare(b.locationId);
                if (loc !== 0) return loc;
                return a.email.toLowerCase().localeCompare(b.email.toLowerCase());
            }),
        });
    };

    const normalizedNames = useMemo(
        () => new Set(lines.map((l) => l.name.trim()).filter(Boolean)),
        [lines],
    );
    const effectiveDefault = normalizedNames.has(defaultBusinessLine.trim())
        ? defaultBusinessLine.trim()
        : '';

    const addNameError =
        draftName && draftName.trim() ? businessLineNameError(draftName.trim()) : null;

    return (
        <VStack align="stretch" spacing={6} pb={16}>
            <SettingsCard
                title="Buy price methodology"
                description="Applies to future quotes, purchase orders, and stock adjustments. Existing purchase order buy prices are not rewritten."
            >
                <SettingsRow
                    label="Method"
                    description={BUY_PRICE_DESCRIPTION}
                    control={
                        <SettingsSegmentedControl
                            segments={BUY_PRICE_SEGMENTS}
                            value={buyPriceMethod}
                            onChange={(value) => update({ buyPriceMethod: value })}
                        />
                    }
                />
            </SettingsCard>

            <SettingsCard
                title="Business lines"
                description="Financial categories to assign to stock adjustments. The default is used for initial stock adjustments."
                headerAction={
                    <PartBayButton
                        variant="solid"
                        colorScheme="partbay-blue"
                        size="sm"
                        text="Add Business Line"
                        onClick={openAdd}
                        isDisabled={draftName !== null}
                    />
                }
                dividers={false}
            >
                {/* py matches SettingsRow so the table lines up with the rows
                    of the cards above it. */}
                <Box py={4}>
                    <RadioGroup
                        value={effectiveDefault}
                        onChange={(value) => update({ defaultBusinessLine: value })}
                    >
                        <SettingsSubTable
                            isInset
                            currentPage={linePaging.currentPage}
                            totalPages={linePaging.totalPages}
                            onPageChange={linePaging.setCurrentPage}
                            footerLeft={
                                lines.length > 0 && !effectiveDefault ? (
                                    <Text fontSize="xs" color="orange.600">
                                        Select a default business line before saving.
                                    </Text>
                                ) : undefined
                            }
                            head={
                                <>
                                    <SettingsTh w="90px" textAlign="center">
                                        Default
                                    </SettingsTh>
                                    <SettingsTh>Name</SettingsTh>
                                    <SettingsTh textAlign="center" w="100px">
                                        Actions
                                    </SettingsTh>
                                </>
                            }
                        >
                            {lines.length === 0 && draftName === null ? (
                                <Tr>
                                    <SettingsTd colSpan={3} py={6} color="gray.500">
                                        No business lines configured yet.
                                    </SettingsTd>
                                </Tr>
                            ) : (
                                linePaging.pageItems.map((line, pageIndex) => {
                                    // Handlers index into the whole list, not the page.
                                    const index =
                                        (linePaging.currentPage - 1) *
                                            SETTINGS_SUBTABLE_PAGE_SIZE +
                                        pageIndex;
                                    const trimmed = line.name.trim();
                                    return (
                                        <Tr key={`${index}-${line.name}`}>
                                            <SettingsTd textAlign="center">
                                                <Radio
                                                    value={trimmed || `__empty-${index}`}
                                                    isDisabled={!trimmed}
                                                    aria-label={`Set ${
                                                        trimmed || 'row'
                                                    } as default`}
                                                />
                                            </SettingsTd>
                                            <SettingsTd>
                                                <SettingsInlineTextEdit
                                                    display={
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="medium"
                                                            color={
                                                                trimmed ? 'gray.800' : 'gray.400'
                                                            }
                                                            noOfLines={1}
                                                        >
                                                            {trimmed || 'Unnamed'}
                                                        </Text>
                                                    }
                                                    value={line.name}
                                                    ariaLabel={`Edit ${
                                                        trimmed || 'business line'
                                                    } name`}
                                                    placeholder="e.g. 12345 - In-Stock"
                                                    validate={(name) =>
                                                        businessLineNameError(name, index)
                                                    }
                                                    onSave={(name) => handleRename(index, name)}
                                                />
                                            </SettingsTd>
                                            <SettingsTd textAlign="center">
                                                <SettingsRowActions
                                                    onDelete={() => handleRemove(index)}
                                                    deleteLabel={`Remove ${
                                                        trimmed || 'business line'
                                                    }`}
                                                />
                                            </SettingsTd>
                                        </Tr>
                                    );
                                })
                            )}

                            {/* The entry row for a new line, on the last page
                                where the saved row will end up. */}
                            {draftName !== null &&
                                linePaging.currentPage === linePaging.totalPages && (
                                    <Tr>
                                        <SettingsTd textAlign="center">
                                            <Radio value="__new" isDisabled />
                                        </SettingsTd>
                                        <SettingsTd>
                                            <VStack align="stretch" spacing={1.5}>
                                                <Input
                                                    size="sm"
                                                    autoFocus
                                                    autoComplete="off"
                                                    value={draftName}
                                                    placeholder="e.g. 12345 - In-Stock"
                                                    aria-label="New business line name"
                                                    isInvalid={addNameError !== null}
                                                    onChange={(e) => setDraftName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleAdd();
                                                        }
                                                        // Escape drops the row rather
                                                        // than leaving it half typed.
                                                        if (e.key === 'Escape') {
                                                            setDraftName(null);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        if (!draftName.trim()) {
                                                            setDraftName(null);
                                                            return;
                                                        }
                                                        handleAdd();
                                                    }}
                                                />
                                                {addNameError && (
                                                    <Text fontSize="xs" color="red.500">
                                                        {addNameError}
                                                    </Text>
                                                )}
                                            </VStack>
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            {/* Keeping focus on the input means
                                                the click discards the row
                                                instead of the blur saving it. */}
                                            <Box
                                                onMouseDown={(e) => e.preventDefault()}
                                            >
                                                <SettingsRowActions
                                                    onDelete={() => setDraftName(null)}
                                                    deleteLabel="Discard new business line"
                                                />
                                            </Box>
                                        </SettingsTd>
                                    </Tr>
                                )}
                        </SettingsSubTable>
                    </RadioGroup>
                </Box>
            </SettingsCard>

            <SettingsCard
                title="Part fulfillment contacts"
                description="Emails notified when a purchase order is placed for that location. Falls back to the location GM when blank."
                dividers={false}
            >
                <Box py={4}>
                    <SettingsSubTable
                        isInset
                        currentPage={locationPaging.currentPage}
                        totalPages={locationPaging.totalPages}
                        onPageChange={locationPaging.setCurrentPage}
                        head={
                            <>
                                <SettingsTh whiteSpace="nowrap" w="1%">
                                    Location
                                </SettingsTh>
                                <SettingsTh>Emails</SettingsTh>
                            </>
                        }
                    >
                        {sortedLocations.length === 0 ? (
                            <Tr>
                                <SettingsTd colSpan={2} py={6} color="gray.500">
                                    No locations configured yet.
                                </SettingsTd>
                            </Tr>
                        ) : (
                            locationPaging.pageItems.map((location) => (
                                <Tr key={location._id}>
                                    <SettingsTd
                                        fontWeight="medium"
                                        color="gray.800"
                                        whiteSpace="nowrap"
                                        w="1%"
                                        verticalAlign="top"
                                    >
                                        {location.title}
                                    </SettingsTd>
                                    <SettingsTd minW={0} w="100%" maxW={0} verticalAlign="top">
                                        <PartFulfillmentContactsField
                                            value={emailsFor(location._id)}
                                            onChange={(emails) =>
                                                handleEmailsChange(location._id, emails)
                                            }
                                            placeholder="Add email…"
                                        />
                                    </SettingsTd>
                                </Tr>
                            ))
                        )}
                    </SettingsSubTable>
                </Box>
            </SettingsCard>
        </VStack>
    );
};

export default InventorySettings;
